<template>
  <div class="fair-page min-h-screen bg-gray-100 px-4 py-6 sm:py-12">
    <div class="mx-auto w-full max-w-md">
      <img class="h-8 mb-8" :src="$brand.logos.horizontal" alt="Securdia" />

      <main v-if="!sent">
        <h1 class="text-2xl font-bold leading-tight text-gray-900">{{ $t("fair_page.title") }}</h1>
        <p class="mt-2 text-gray-600 leading-snug">{{ $t("fair_page.intro") }}</p>

        <ValidationObserver ref="form" tag="form" class="mt-6" novalidate @submit.prevent="submit">
          <ValidationProvider v-for="field in fields" :key="field.key" :name="$t(`fair_page.fields.${field.key}`)" :vid="field.key" :rules="field.rules" v-slot="{ errors }" tag="div" class="mb-4">
            <label :for="`fair-${field.key}`" class="block text-sm font-bold text-gray-900 mb-1">{{ $t(`fair_page.fields.${field.key}`) }}<template v-if="field.rules.includes('required')"> *</template></label>
            <input
              :id="`fair-${field.key}`"
              v-model="form[field.key]"
              :type="field.type"
              :autocomplete="field.autocomplete"
              :inputmode="field.inputmode"
              :autocapitalize="field.autocapitalize"
              :placeholder="$t(`fair_page.placeholders.${field.key}`)"
              maxlength="200"
              class="fair-input"
              :class="{ 'fair-input-error': errors.length }"
            />
            <p v-if="errors.length" class="mt-1 text-sm text-red-600">{{ errors[0] }}</p>
          </ValidationProvider>

          <!-- Honeypot: hidden from people, bots fill it in and the server drops the request -->
          <div class="fair-honeypot" aria-hidden="true">
            <label for="fair-website">Website</label>
            <input id="fair-website" v-model="form.website" type="text" tabindex="-1" autocomplete="off" />
          </div>

          <p v-if="errorMessage" class="mt-5 mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{{ errorMessage }}</p>

          <button type="submit" :disabled="isSending" class="mt-5 w-full rounded-lg bg-primary-600 hover:bg-primary-700 py-3 text-base font-bold text-white shadow transition duration-200 disabled:opacity-60">
            {{ isSending ? $t("fair_page.sending") : $t("fair_page.submit") }}
          </button>
        </ValidationObserver>

        <p class="mt-5 text-xs text-gray-600 leading-snug">
          {{ $t("fair_page.consent") }}
          <a href="https://securdia.se/integritetspolicy/" target="_blank" rel="noopener" class="text-primary-600 underline">{{ $t("fair_page.privacy_policy") }}</a>.
        </p>
      </main>

      <main v-else class="text-center pt-8">
        <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <svg class="h-8 w-8" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
        </div>
        <h1 class="mt-5 text-2xl font-bold text-gray-900">{{ $t("fair_page.thanks_title", { name: firstName }) }}</h1>
        <p class="mt-2 text-gray-600">{{ $t("fair_page.thanks_message") }}</p>
        <button type="button" class="mt-8 text-primary-600 font-bold underline" @click="reset">{{ $t("fair_page.send_another") }}</button>
      </main>
    </div>
  </div>
</template>

<script>
// Public contact form for trade fairs, opened by QR code at the booth.
const emptyForm = () => ({ name: "", email: "", phone: "", website: "" });

export default {
  name: "FairContact",
  title() {
    return this.$t("fair_page.page_title");
  },
  data() {
    return {
      form: emptyForm(),
      fields: [
        { key: "name", type: "text", autocomplete: "name", autocapitalize: "words", rules: "required" },
        { key: "email", type: "email", autocomplete: "email", inputmode: "email", autocapitalize: "off", rules: "required|email" },
        { key: "phone", type: "tel", autocomplete: "tel", inputmode: "tel", rules: "" },
      ],
      isSending: false,
      sent: false,
      errorMessage: "",
    };
  },
  computed: {
    firstName() {
      return this.form.name.trim().split(/\s+/)[0];
    },
  },
  methods: {
    async submit() {
      this.errorMessage = "";
      const valid = await this.$refs.form.validate();
      if (!valid) return;

      this.isSending = true;
      try {
        await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/info/fair`, this.form);
        this.sent = true;
        window.scrollTo(0, 0);
      } catch (error) {
        const status = error && error.response && error.response.status;
        this.errorMessage = status === 429 ? this.$t("fair_page.error_rate_limit") : this.$t("fair_page.error");
      } finally {
        this.isSending = false;
      }
    },
    reset() {
      this.form = emptyForm();
      this.sent = false;
      this.$nextTick(() => this.$refs.form.reset());
    },
  },
};
</script>

<style scoped>
/* global.scss sets the serif title font on these; this page is sans throughout */
.fair-page h1,
.fair-page label,
.fair-page a {
  font-family: "Source Sans Pro", sans-serif;
}
.fair-input {
  display: block;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #fff;
  padding: 0.7rem 0.85rem;
  /* 16px keeps iOS Safari from zooming in on focus */
  font-size: 16px;
  color: #111827;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.fair-input::placeholder {
  color: #9ca3af;
}
.fair-input:focus {
  outline: none;
  border-color: #1a8de9;
  box-shadow: 0 0 0 3px rgba(26, 141, 233, 0.2);
}
.fair-input-error {
  border-color: #dc2626;
}
.fair-honeypot {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
</style>
